import {
  AutoAwesome as AIIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Flag as FlagIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import type {
  AlternativeGoal,
  GoalSuggestionResponse,
  GoalTargetMetric,
  TrainingGoal,
} from '@shared/types';
import React, { useEffect, useState } from 'react';
import apiClient from '../../services/api';
import { logger } from '../../utils/logger';

interface GoalDialogProps {
  open: boolean;
  onClose: () => void;
  onGoalSaved: (goal: TrainingGoal) => void;
  currentGoal?: TrainingGoal;
  initialGoalText?: string;
}

/**
 * Format a target metric for display
 * Uses explicit time units: 4h30m, 4m20s, 4m30s/km (not colon format)
 */
function formatMetric(metric: GoalTargetMetric): string {
  if (metric.display) {
    return metric.display;
  }

  switch (metric.unit) {
    case 'sec/km': {
      const mins = Math.floor(metric.value / 60);
      const secs = Math.round(metric.value % 60);
      return secs > 0 ? `${mins}m${secs}s/km` : `${mins}m/km`;
    }
    case 'W':
      return `${metric.value}W`;
    case 'km/h':
      return `${metric.value} km/h`;
    case 'km':
      return `${metric.value} km`;
    case 'sec': {
      const hours = Math.floor(metric.value / 3600);
      const minutes = Math.floor((metric.value % 3600) / 60);
      const seconds = Math.round(metric.value % 60);
      if (hours > 0) {
        return seconds > 0 ? `${hours}h${minutes}m${seconds}s` : `${hours}h${minutes}m`;
      }
      return seconds > 0 ? `${minutes}m${seconds}s` : `${minutes}m`;
    }
    default:
      return `${metric.value} ${metric.unit}`;
  }
}

/**
 * Get confidence color based on label
 */
function getConfidenceColor(label: string): 'success' | 'warning' | 'error' | 'default' {
  switch (label) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
    case 'very low':
      return 'error';
    default:
      return 'default';
  }
}

const GoalDialog: React.FC<GoalDialogProps> = ({
  open,
  onClose,
  onGoalSaved,
  currentGoal,
  initialGoalText,
}) => {
  const [goalInput, setGoalInput] = useState(initialGoalText || '');
  const [dateInput, setDateInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Getting AI suggestions...');
  const [isSaving, setIsSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<GoalSuggestionResponse | null>(null);
  const [isMockData, setIsMockData] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<{
    goal: string;
    targetDate: string;
    targetMetric: GoalTargetMetric;
    confidenceScore: number;
    confidenceLabel: 'high' | 'medium' | 'low' | 'very low';
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Update goalInput when initialGoalText changes (e.g., when clicking a goal idea)
  useEffect(() => {
    if (open && initialGoalText) {
      setGoalInput(initialGoalText);
    }
  }, [open, initialGoalText]);

  const handleGetSuggestions = async () => {
    setIsLoading(true);
    setLoadingMessage('Connecting to AI agent...');
    setError(null);
    setSuggestions(null);
    setSelectedGoal(null);
    setIsMockData(false);

    // Update loading message periodically to show progress
    const messageInterval = setInterval(() => {
      setLoadingMessage(prev => {
        const messages = [
          'Connecting to AI agent...',
          'Analyzing your profile...',
          'Generating personalized goals...',
          'Almost there...',
          'This may take up to a minute...',
        ];
        const currentIndex = messages.indexOf(prev);
        return messages[Math.min(currentIndex + 1, messages.length - 1)];
      });
    }, 8000); // Change message every 8 seconds

    try {
      logger.info('Requesting goal suggestions', { goal: goalInput, date: dateInput });

      const response = await apiClient.getGoalSuggestions(
        goalInput || undefined,
        dateInput || undefined
      );

      if (response.success && response.data) {
        // Validate the response structure
        if (!response.data.recommendedGoal || !response.data.recommendedGoal.goal) {
          setError('AI returned an incomplete response. Please try again.');
          logger.error('Invalid AI response structure', new Error('Missing recommendedGoal'), {
            data: response.data,
          });
          return;
        }

        setSuggestions(response.data);
        setIsMockData(response.isMock === true);
        // Auto-select the recommended goal
        setSelectedGoal(response.data.recommendedGoal);
        logger.info('Goal suggestions received', {
          recommendedGoal: response.data.recommendedGoal.goal,
          alternatives: response.data.alternativeGoals?.length || 0,
          isMock: response.isMock,
        });
      } else {
        setError(response.message || 'Failed to get goal suggestions');
        logger.error('Goal suggestions failed', new Error(response.message || 'Unknown error'));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      logger.error('Goal suggestions error', err as Error);
    } finally {
      clearInterval(messageInterval);
      setIsLoading(false);
      setLoadingMessage('Getting AI suggestions...');
    }
  };

  const handleSelectAlternative = (alt: AlternativeGoal) => {
    if (suggestions) {
      setSelectedGoal({
        goal: alt.goal,
        targetDate: suggestions.recommendedGoal.targetDate, // Use same date
        targetMetric: alt.targetMetric,
        confidenceScore: suggestions.recommendedGoal.confidenceScore - 10, // Slightly lower
        confidenceLabel: suggestions.recommendedGoal.confidenceLabel,
      });
    }
  };

  const handleSaveGoal = async () => {
    if (!selectedGoal) return;

    setIsSaving(true);
    setError(null);

    try {
      const goalToSave: TrainingGoal = {
        ...selectedGoal,
        createdAt: new Date().toISOString(),
      };

      const response = await apiClient.saveTrainingGoal(goalToSave);

      if (response.success && response.data) {
        logger.info('Goal saved successfully', { goal: response.data.goal });
        onGoalSaved(response.data);
        handleClose();
      } else {
        setError(response.message || 'Failed to save goal');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      logger.error('Save goal error', err as Error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setGoalInput('');
    setDateInput('');
    setSuggestions(null);
    setSelectedGoal(null);
    setError(null);
    setIsMockData(false);
    onClose();
  };

  // Calculate min date (today) and default date (12 weeks from now)
  const today = new Date().toISOString().split('T')[0];
  const defaultDate = new Date();
  defaultDate.setDate(defaultDate.getDate() + 84);
  const defaultDateStr = defaultDate.toISOString().split('T')[0];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FlagIcon color="primary" />
        Define Training Goal
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {/* Current Goal Display */}
          {currentGoal && !suggestions && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Current Goal:</Typography>
              <Typography variant="body2">
                {currentGoal.goal} - Target: {formatMetric(currentGoal.targetMetric)} by{' '}
                {new Date(currentGoal.targetDate).toLocaleDateString()}
              </Typography>
            </Alert>
          )}

          {/* Input Section */}
          {!suggestions && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Describe your goal (optional) and target date, then let AI suggest the best training
                objectives for you.
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    label="Goal (optional)"
                    placeholder="e.g., Run a sub-4:00 marathon, Improve FTP by 20W"
                    value={goalInput}
                    onChange={e => setGoalInput(e.target.value)}
                    size="small"
                    aria-label="Enter your training goal"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Target Date"
                    value={dateInput || defaultDateStr}
                    onChange={e => setDateInput(e.target.value)}
                    size="small"
                    inputProps={{ min: today }}
                    InputLabelProps={{ shrink: true }}
                    aria-label="Select target date for your goal"
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGetSuggestions}
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={16} /> : <AIIcon />}
                  aria-label="Get AI goal suggestions"
                >
                  {isLoading ? loadingMessage : 'Get AI Suggestions'}
                </Button>
                {isLoading && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    AI analysis may take up to 60 seconds
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Mock Data Indicator */}
          {isMockData && suggestions && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Sample Data:</strong> AI agent is not configured. These are example
                suggestions for demonstration purposes.
              </Typography>
            </Alert>
          )}

          {/* Suggestions Display */}
          {suggestions && suggestions.recommendedGoal && (
            <Box>
              {/* Recommended Goal */}
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <TrendingUpIcon fontSize="small" color="primary" />
                Recommended Goal
              </Typography>

              <Card
                variant="outlined"
                sx={{
                  mb: 2,
                  borderColor:
                    selectedGoal?.goal === suggestions.recommendedGoal.goal
                      ? 'primary.main'
                      : 'divider',
                  borderWidth: selectedGoal?.goal === suggestions.recommendedGoal.goal ? 2 : 1,
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.light' },
                }}
                onClick={() => setSelectedGoal(suggestions.recommendedGoal)}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                        {suggestions.recommendedGoal.goal}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Target:{' '}
                        {new Date(suggestions.recommendedGoal.targetDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h5" color="primary.main" sx={{ fontWeight: 600 }}>
                        {formatMetric(suggestions.recommendedGoal.targetMetric)}
                      </Typography>
                      <Chip
                        label={`${suggestions.recommendedGoal.confidenceScore}% ${suggestions.recommendedGoal.confidenceLabel}`}
                        size="small"
                        color={getConfidenceColor(suggestions.recommendedGoal.confidenceLabel)}
                      />
                    </Box>
                  </Box>
                  {selectedGoal?.goal === suggestions.recommendedGoal.goal && (
                    <CheckIcon color="primary" sx={{ position: 'absolute', top: 8, right: 8 }} />
                  )}
                </CardContent>
              </Card>

              {/* Alternative Goals */}
              {suggestions.alternativeGoals.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Alternative Goals
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    {suggestions.alternativeGoals.map((alt, idx) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                        <Card
                          variant="outlined"
                          sx={{
                            cursor: 'pointer',
                            borderColor:
                              selectedGoal?.goal === alt.goal ? 'primary.main' : 'divider',
                            borderWidth: selectedGoal?.goal === alt.goal ? 2 : 1,
                            '&:hover': { borderColor: 'primary.light' },
                          }}
                          onClick={() => handleSelectAlternative(alt)}
                        >
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {alt.goal}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {alt.reason}
                                </Typography>
                              </Box>
                              <Typography variant="body1" color="primary.main" fontWeight={600}>
                                {formatMetric(alt.targetMetric)}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Rationale */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Analysis
              </Typography>
              <Box sx={{ bgcolor: 'background.default', p: 1.5, borderRadius: 1, mb: 2 }}>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  <strong>Fitness:</strong> {suggestions.rationale.fitnessAssessment}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  <strong>Fatigue:</strong> {suggestions.rationale.fatigueAssessment}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  <strong>Constraints:</strong> {suggestions.rationale.constraintAlignment}
                </Typography>
                {suggestions.rationale.riskNotes && (
                  <Typography variant="caption" display="block" color="warning.main">
                    <strong>⚠️ Note:</strong> {suggestions.rationale.riskNotes}
                  </Typography>
                )}
              </Box>

              {/* Next Step */}
              <Alert severity="info" icon={<TrendingUpIcon />}>
                <Typography variant="body2">
                  <strong>Next Step:</strong> {suggestions.nextStepSuggestion}
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {suggestions ? (
          <>
            <Button
              onClick={() => {
                setSuggestions(null);
                setSelectedGoal(null);
              }}
              startIcon={<ClearIcon />}
              aria-label="Start over with new goal input"
            >
              Start Over
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleClose} aria-label="Cancel goal selection">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveGoal}
              disabled={!selectedGoal || isSaving}
              startIcon={isSaving ? <CircularProgress size={16} /> : <CheckIcon />}
              aria-label="Save selected training goal"
            >
              {isSaving ? 'Saving...' : 'Save Goal'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} aria-label="Close goal dialog">
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GoalDialog;
